import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  createPortal,
} from 'react-dom';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import type {
  UpdatePatientPCCConsentsRequestDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import {
  useGetPCCActivationSettings,
  useGetPCCConsentsByPatientId,
  useUpdatePCCConsents,
} from 'views/EMR/hooks/pcc';
import ConsentAccordionSection from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/ConsentAccordionSection';
import ConsentCompactSection from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/ConsentCompactSection';
import SaveIcon from 'core/icons/save';
import {
  defaultPCCFormValues,
  formatConsentUpdatedByName,
  normalizePCCConsents,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/helper';
import type {
  PCCForm,
  PCCProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/types';
import {
  useNotify,
  usePrompt,
} from 'core/hooks';

type UnsavedChangesPromptProps = {
  when: boolean;
};

function UnsavedChangesPrompt({ when }: UnsavedChangesPromptProps) {
  usePrompt(when);
  return null;
}

export default function PCC({
  patient,
  viewMode = 'normal',
  footerSlotId,
  disabled = false,
}: PCCProps) {
  const patientId = patient.patientId ?? 0;
  const notify = useNotify();
  const {
    data,
    isLoading,
    refetch,
  } = useGetPCCConsentsByPatientId(patientId);
  const { mutateAsync: updatePCCConsents } = useUpdatePCCConsents();
  const { data: activationSettings } = useGetPCCActivationSettings();
  const isCcmApplicable = activationSettings?.isCcmApplicable === true;

  const ccmConsent = data?.ccm;
  const consentFlag = data?.consent;

  const consentGivenByName = formatConsentUpdatedByName(consentFlag?.updatedBy);

  const formProps = useForm<PCCForm>({
    defaultValues: defaultPCCFormValues,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
  } = formProps;

  const hasFooterSlot = !isNil(footerSlotId);

  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  const skipAutoSaveRef = useRef(true);

  useLayoutEffect(() => {
    if (hasFooterSlot) {
      setFooterSlot(document.getElementById(footerSlotId));
    }
  }, [footerSlotId, hasFooterSlot, patientId]);

  useEffect(() => {
    skipAutoSaveRef.current = true;
    reset(normalizePCCConsents(data));
    skipAutoSaveRef.current = false;
  }, [data, reset]);

  const onSave = async (formValues: PCCForm) => {
    try {
      const payload: UpdatePatientPCCConsentsRequestDTO = {
        patientId,
        hasConsentedToCommunication: formValues.hasConsentedToCommunication,
        ccm: formValues.ccm,
      };
      await updatePCCConsents(payload);
      await refetch?.();
      notify({
        variant: 'success',
        message: 'PCC consents updated successfully',
      });

      if (!hasFooterSlot) {
        reset(formValues, {
          keepDirty: false,
          keepTouched: false,
          keepIsSubmitted: false,
        });
      }
    } catch {
      // Preserve form state on failed autosave or manual save
    }
  };

  useEffect(() => {
    if (hasFooterSlot) {
      return undefined;
    }

    const subscription = watch((_value, { name, type }) => {
      if (
        skipAutoSaveRef.current
        || type !== 'change'
        || isNil(name)
        || (name !== 'hasConsentedToCommunication' && !name.startsWith('ccm.'))
      ) {
        return;
      }

      handleSubmit(onSave)();
    });

    return () => { subscription.unsubscribe(); };
  }, [hasFooterSlot, watch, handleSubmit]);

  const saveButtonText = getSubmitButtonText(false, isSubmitting);
  const isToggleDisabled = disabled || isSubmitting;

  return (
    <FormProvider {...formProps}>
      {hasFooterSlot && <UnsavedChangesPrompt when={isDirty} />}
      <div className={clsx('flex flex-col w-full', { 'gap-4': viewMode === 'normal' })}>
        {viewMode === 'compact' ? (
          <ConsentCompactSection
            consent={ccmConsent}
            consentFlag={consentFlag}
            consentGivenByName={consentGivenByName}
            disabled={isToggleDisabled}
            isCcmApplicable={isCcmApplicable}
            isLoading={isLoading}
          />
        ) : (
          <ConsentAccordionSection
            header="CCM Communication"
            consent={ccmConsent}
            consentFlag={consentFlag}
            consentGivenByName={consentGivenByName}
            showEligibility
            isCcmApplicable={isCcmApplicable}
            isLoading={isLoading}
            patientId={patientId}
            patientEmail={patient.email}
            patientPhone={patient.phoneNumber?.phoneNumberWithAreaCode}
          />
        )}
      </div>
      {!isNil(footerSlot) ? createPortal(
        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          className={isSubmitting ? 'is-pending' : ''}
          onClick={() => { handleSubmit(onSave)(); }}
          startIcon={<SaveIcon  className="!w-4 !h-4"/>}
        >
          {saveButtonText}
        </Button>,
        footerSlot,
      ) : null}
    </FormProvider>
  );
}
